/*
  Tab behaviour for sections/product-tabs.liquid — WCAG APG tabs with a roving tabindex.

  Progressive enhancement only. The section ships the first panel WITHOUT `hidden` and the rest
  WITH it, so with no JavaScript a visitor still sees the designed band with the first tab open.
  This file only adds switching.

  Deliberately does NOT bail on prefers-reduced-motion or Shopify.designMode, unlike
  about-timeline.js: this is function, not decoration. There is no transition to disable, and the
  tabs have to work while a merchant is editing them.
*/
if (!customElements.get('product-tabs')) {
  customElements.define(
    'product-tabs',
    class ProductTabs extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.panels = this.tabs.map((tab) => this.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls'))}`));

        // One tab is a section label, not a control — nothing to switch between.
        if (this.tabs.length < 2) return;

        this.onClick = this.onClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onBlockSelect = this.onBlockSelect.bind(this);

        this.tabs.forEach((tab, i) => {
          tab.addEventListener('click', () => this.activate(i));
        });
        this.addEventListener('keydown', this.onKeydown);

        // Clicking a tab block in the theme-editor sidebar should reveal its panel.
        document.addEventListener('shopify:block:select', this.onBlockSelect);
      }

      disconnectedCallback() {
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
      }

      onClick(event) {
        const index = this.tabs.indexOf(event.currentTarget);
        if (index >= 0) this.activate(index);
      }

      onKeydown(event) {
        const current = this.tabs.indexOf(document.activeElement);
        if (current < 0) return;

        const rtl = getComputedStyle(this).direction === 'rtl';
        let next = null;

        switch (event.key) {
          case 'ArrowRight':
            next = rtl ? current - 1 : current + 1;
            break;
          case 'ArrowLeft':
            next = rtl ? current + 1 : current - 1;
            break;
          case 'Home':
            next = 0;
            break;
          case 'End':
            next = this.tabs.length - 1;
            break;
          default:
            return;
        }

        event.preventDefault();
        this.activate((next + this.tabs.length) % this.tabs.length, { focus: true });
      }

      onBlockSelect(event) {
        if (!this.contains(event.target)) return;
        const tab = event.target.closest('[role="tab"]');
        const index = this.tabs.indexOf(tab);
        if (index >= 0) this.activate(index);
      }

      activate(index, { focus = false } = {}) {
        this.tabs.forEach((tab, i) => {
          tab.setAttribute('aria-selected', String(i === index));
          tab.tabIndex = i === index ? 0 : -1;
        });
        this.panels.forEach((panel, i) => {
          if (panel) panel.toggleAttribute('hidden', i !== index);
        });
        if (focus) this.tabs[index].focus();
      }
    }
  );
}
